import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useOrganizationStore } from '../store/organizationStore';
import { Team } from '../types';

export interface UserPermissions {
  /** User is org owner or org admin — can do everything at org level */
  isOrgAdmin: boolean;
  /** User's explicit org role */
  orgRole: 'owner' | 'admin' | 'member' | null;
  /** Returns true if user is a team admin (or org admin) for a given team */
  isTeamAdmin: (team: Team) => boolean;
  /** Returns true if user can manage (edit/add/remove members) a given team */
  canManageTeam: (team: Team) => boolean;
}

export function useUserPermissions(): UserPermissions {
  const { user } = useAuthStore();
  const { currentOrganization } = useOrganizationStore();

  return useMemo(() => {
    const orgRole = currentOrganization?.my_role ?? null;
    const isOrgAdmin = orgRole === 'owner' || orgRole === 'admin';

    const isTeamAdmin = (team: Team): boolean => {
      if (isOrgAdmin) return true;
      if (!user) return false;
      return team.is_team_admin || team.admins?.some((a) => a.id === user.id) || false;
    };

    const canManageTeam = (team: Team): boolean => isTeamAdmin(team);

    return { isOrgAdmin, orgRole, isTeamAdmin, canManageTeam };
  }, [user, currentOrganization]);
}
